/**
 * Auth data layer — TanStack Query hooks over the /auth API.
 *
 * The current user lives in ONE query cache (authKeys.me). Every screen reads
 * the same cached identity instead of re-fetching /me, and mutations update that
 * cache so the UI reflects a change instantly and consistently.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, PublicUser } from '@/lib/api';
import { billingKeys } from '@/billing/hooks';

export const authKeys = {
  me: ['auth', 'me'] as const,
};

/** null = definitively signed out (401). A thrown error = a real problem. */
async function fetchMe(): Promise<PublicUser | null> {
  try {
    const r = await api.me();
    return r.user;
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return null;
    throw e;
  }
}

export function useCurrentUser() {
  return useQuery({ queryKey: authKeys.me, queryFn: fetchMe, retry: false });
}

export function useSignup() {
  // No session is created on sign-up (the account must verify its email first),
  // so there is nothing to write into the identity cache — the SPA redirects to
  // login afterwards.
  return useMutation({ mutationFn: api.signup });
}

export function useVerifyEmail() {
  return useMutation({ mutationFn: api.verifyEmail });
}

export function useResendVerification() {
  return useMutation({ mutationFn: api.resendVerification });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.login,
    onSuccess: (r) => {
      qc.setQueryData(authKeys.me, r.user);
      // Refetch subscription under the NEW session so post-login routing (pricing
      // vs dashboard, in RequireGuest) sees the real status — not the logged-out
      // 401 null it cached on the login screen.
      void qc.invalidateQueries({ queryKey: billingKeys.status });
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.logout,
    // Clear identity regardless of the server's answer — the intent is to leave.
    onSettled: () => qc.setQueryData(authKeys.me, null),
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.updateProfile,
    onSuccess: (r) => qc.setQueryData(authKeys.me, r.user),
  });
}

export function useChangePassword() {
  return useMutation({ mutationFn: api.changePassword });
}

export function useForgotPassword() {
  return useMutation({ mutationFn: api.forgotPassword });
}

export function useResetPassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.resetPassword,
    // A reset drops all sessions server-side; reflect that locally.
    onSuccess: () => qc.setQueryData(authKeys.me, null),
  });
}
